import type {
  GetControlResultActivitiesResponseDto,
  GetControlResultActivitiesResponseItemDto,
  GetControlResultActivitiesResponseRelationshipsDto,
  PriorityResponseDto,
  UserId,
} from "@/spz-api/model";
import type { RichText } from "@spz/shared-domain";

export class ControlResultActivityListItem {
  id: string;
  createdAt: Date;

  actor: ControlResultActivityActor;
  comment: ControlResultActivityComment | null;
  attachment: ControlResultActivityAttachment | null;
  workflowStepChange: ControlResultActivityWorkflowStepChange | null;
  workflowConclusionChange: ControlResultActivityWorkflowConclusionChange | null;
  ownerChange: ControlResultActivityOwnerChange | null;
  priorityChange: ControlResultActivityPriorityChange | null;

  constructor(params: {
    id: string;
    createdAt: Date;
    actor: ControlResultActivityActor;
    comment: ControlResultActivityComment | null;
    attachment: ControlResultActivityAttachment | null;
    workflowStepChange: ControlResultActivityWorkflowStepChange | null;
    workflowConclusionChange: ControlResultActivityWorkflowConclusionChange | null;
    ownerChange: ControlResultActivityOwnerChange | null;
    priorityChange: ControlResultActivityPriorityChange | null;
  }) {
    this.id = params.id;
    this.createdAt = params.createdAt;
    this.actor = params.actor;
    this.comment = params.comment;
    this.attachment = params.attachment;
    this.workflowStepChange = params.workflowStepChange;
    this.workflowConclusionChange = params.workflowConclusionChange;
    this.ownerChange = params.ownerChange;
    this.priorityChange = params.priorityChange;
  }

  canEdit(userId: UserId): boolean {
    if (this.workflowStepChange?.from || this.workflowStepChange?.to) {
      return false;
    }
    if (
      this.workflowConclusionChange?.from ||
      this.workflowConclusionChange?.to
    ) {
      return false;
    }
    if (this.ownerChange?.from || this.ownerChange?.to) {
      return false;
    }
    if (this.priorityChange?.from || this.priorityChange?.to) {
      return false;
    }
    return this.actor.id === String(userId) && this.comment !== null;
  }

  canDelete(userId: UserId): boolean {
    if (this.workflowStepChange?.from || this.workflowStepChange?.to) {
      return false;
    }
    if (
      this.workflowConclusionChange?.from ||
      this.workflowConclusionChange?.to
    ) {
      return false;
    }
    if (this.ownerChange?.from || this.ownerChange?.to) {
      return false;
    }
    if (this.priorityChange?.from || this.priorityChange?.to) {
      return false;
    }
    return this.actor.id === String(userId) && this.comment !== null;
  }

  get commentKey(): string {
    return `${this.id}-${this.comment?.updatedAt?.getTime() || this.createdAt.getTime()}`;
  }

  get attachmentOnly(): boolean {
    return this.attachment !== null && this.comment === null;
  }
}

export type ControlResultActivityActor = {
  id: string;
  fullName: string;
  profilePictureUrl: string | null;
};

export type ControlResultActivityComment = {
  id: string;
  content: RichText;
  hasBeenUpdated: boolean;
  updatedAt: Date | null;
};

export type ControlResultActivityAttachment = {
  id: string;
  name: string;
  iconName: string;
};

export type ControlResultActivityWorkflowStepChange = {
  from: {
    labelKeyName: string;
    variant: string;
  } | null;
  to: {
    labelKeyName: string;
    variant: string;
  } | null;
};

export type ControlResultActivityWorkflowConclusionChange = {
  from: {
    labelKeyName: string;
  } | null;
  to: {
    labelKeyName: string;
  } | null;
};

export type ControlResultActivityOwnerChange = {
  from: {
    firstName: string;
    lastName: string;
    profilePictureUrl: string | null;
  } | null;
  to: {
    firstName: string;
    lastName: string;
    profilePictureUrl: string | null;
  } | null;
};

export type ControlResultActivityPriorityChange = {
  from: PriorityResponseDto | null;
  to: PriorityResponseDto | null;
};

export class ControlResultActivityMapper {
  static toListItem(
    data: GetControlResultActivitiesResponseItemDto,
    relationships: GetControlResultActivitiesResponseRelationshipsDto,
  ): ControlResultActivityListItem {
    const actor = relationships.users.find(
      (user) => String(user.id) === String(data.actor),
    );
    const fromWorkflowStep = relationships.workflowSteps.find(
      (step) => String(step.id) === String(data.workflowStepChange?.fromId),
    );
    const toWorkflowStep = relationships.workflowSteps.find(
      (step) => String(step.id) === String(data.workflowStepChange?.toId),
    );
    const fromWorkflowConclusion = relationships.conclusions.find(
      (conclusion) =>
        String(conclusion.id) === String(data.workflowConclusionChange?.fromId),
    );
    const toWorkflowConclusion = relationships.conclusions.find(
      (conclusion) =>
        String(conclusion.id) === String(data.workflowConclusionChange?.toId),
    );
    const fromOwner = relationships.users.find(
      (user) => String(user.id) === String(data.ownerChange?.fromId),
    );
    const toOwner = relationships.users.find(
      (user) => String(user.id) === String(data.ownerChange?.toId),
    );
    const fromPriority = relationships.priorities.find(
      (priority) => String(priority.id) === String(data.priorityChange?.fromId),
    );
    const toPriority = relationships.priorities.find(
      (priority) => String(priority.id) === String(data.priorityChange?.toId),
    );

    return new ControlResultActivityListItem({
      id: data.operationId ?? crypto.randomUUID(),
      createdAt: new Date(data.createdAt),
      actor: actor
        ? {
            id: String(actor.id),
            fullName: `${actor.firstName} ${actor.lastName}`,
            profilePictureUrl: actor.profilePictureUrl
              ? String(actor.profilePictureUrl)
              : null,
          }
        : {
            id: crypto.randomUUID(),
            fullName: "Supervizor",
            profilePictureUrl: "https://www.supervizor.com/hubfs/favicon.ico",
          },
      comment: data.comment
        ? {
            id: data.comment.id,
            content: data.comment.content as unknown as RichText, // @comment: safe cast because the content is always a RichText object
            hasBeenUpdated:
              Date.parse(data.comment.updatedAt) > Date.parse(data.createdAt),
            updatedAt: data.comment.updatedAt
              ? new Date(data.comment.updatedAt)
              : null,
          }
        : null,
      attachment: data.attachment
        ? {
            id: data.attachment.id,
            name: decodeURIComponent(data.attachment.fileName),
            iconName: data.attachment.contentType, // TODO: get icon name from content type
          }
        : null,
      workflowStepChange: data.workflowStepChange
        ? {
            from: fromWorkflowStep
              ? {
                  labelKeyName: fromWorkflowStep.labelKeyName,
                  variant: fromWorkflowStep.variant,
                }
              : null,
            to: toWorkflowStep
              ? {
                  labelKeyName: toWorkflowStep.labelKeyName,
                  variant: toWorkflowStep.variant,
                }
              : null,
          }
        : null,
      workflowConclusionChange: data.workflowConclusionChange
        ? {
            from: fromWorkflowConclusion
              ? {
                  labelKeyName: String(fromWorkflowConclusion.labelKeyName),
                }
              : null,
            to: toWorkflowConclusion
              ? {
                  labelKeyName: String(toWorkflowConclusion.labelKeyName),
                }
              : null,
          }
        : null,
      ownerChange: data.ownerChange
        ? {
            from: fromOwner
              ? {
                  firstName: fromOwner.firstName,
                  lastName: fromOwner.lastName,
                  profilePictureUrl: fromOwner.profilePictureUrl
                    ? String(fromOwner.profilePictureUrl)
                    : null,
                }
              : null,
            to: toOwner
              ? {
                  firstName: toOwner.firstName,
                  lastName: toOwner.lastName,
                  profilePictureUrl: toOwner.profilePictureUrl
                    ? String(toOwner.profilePictureUrl)
                    : null,
                }
              : null,
          }
        : null,
      priorityChange: data.priorityChange
        ? {
            from: fromPriority ?? null,
            to: toPriority ?? null,
          }
        : null,
    });
  }

  static toListItems(
    data: GetControlResultActivitiesResponseDto,
  ): ControlResultActivityListItem[] {
    return data.data.map((item) =>
      ControlResultActivityMapper.toListItem(item, data.relationships),
    );
  }
}
